"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { ChevronDown, ChevronUp, X, ImageIcon, FileText, RotateCcw, CheckCircle2, Loader2 } from "lucide-react";
import { uploadQueueStore, retryUpload, dismissUpload, type UploadItem } from "@/lib/upload-queue";

export function UploadProgressPopup() {
  const [items, setItems] = useState<UploadItem[]>([]);
  const [collapsed, setCollapsed] = useState(false);
  const [closed, setClosed] = useState(false);
  // Chặn bấm lặp lại khi mạng chậm — mỗi click gọi router.push lại, không có
  // guard này thì user tưởng lần bấm trước chưa ăn nên bấm dồn dập, càng làm
  // nhiều request điều hướng chồng nhau, còn chờ lâu hơn thay vì nhanh hơn.
  const [navigating, setNavigating] = useState(false);
  const router = useRouter();

  const handleOpen = async (url: string) => {
    if (navigating) return;
    setNavigating(true);
    try {
      await router.push(url);
    } finally {
      setNavigating(false);
    }
  };

  useEffect(() => {
    uploadQueueStore.subscribe(setItems);
    return () => uploadQueueStore.unsubscribe(setItems);
  }, []);

  // Re-open the panel automatically whenever a brand new item arrives —
  // otherwise a manually-closed popup would silently swallow the next upload.
  useEffect(() => {
    if (items.some((i) => i.status === "queued" || i.status === "uploading" || i.status === "processing")) setClosed(false);
  }, [items]);

  if (items.length === 0 || closed) return null;

  const activeCount = items.filter((i) => i.status === "queued" || i.status === "uploading" || i.status === "processing").length;

  return (
    <div className="fixed bottom-4 right-4 z-[100] w-[360px] bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100 bg-slate-50">
        <p className="text-sm font-semibold text-slate-700">
          {activeCount > 0 ? `Đang tải lên ${activeCount} mục` : "Đã tải lên xong"}
        </p>
        <div className="flex items-center gap-1">
          <button onClick={() => setCollapsed((c) => !c)} className="text-slate-400 hover:text-slate-600 p-1" aria-label="Thu gọn">
            {collapsed ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          <button onClick={() => setClosed(true)} className="text-slate-400 hover:text-slate-600 p-1" aria-label="Đóng">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {!collapsed && (
        <div className="max-h-[320px] overflow-y-auto divide-y divide-slate-100">
          {items.map((item) => {
            const Icon = item.fileKind === "pdf" ? FileText : ImageIcon;
            const clickable = item.status === "success" && !navigating;
            return (
              <div
                key={item.id}
                onClick={clickable ? () => handleOpen(item.targetUrl) : undefined}
                className={`flex items-center gap-3 px-4 py-2.5 ${clickable ? "cursor-pointer hover:bg-slate-50" : ""}`}
              >
                <Icon className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-700 truncate" title={item.fileName}>{item.fileName}</p>
                  {item.status === "error" ? (
                    <p className="text-xs text-red-500 mt-0.5">{item.error || "Tải lên thất bại"}</p>
                  ) : item.status === "success" ? (
                    <p className="text-xs text-green-600 mt-0.5">Hoàn tất — bấm để xem</p>
                  ) : item.status === "processing" ? (
                    <p className="text-xs text-blue-500 mt-0.5 flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" /> Đang xử lý...
                    </p>
                  ) : (
                    <div className="h-1.5 bg-slate-100 rounded-full mt-1 overflow-hidden">
                      <div className="h-full bg-blue-500 transition-all" style={{ width: `${item.progress}%` }} />
                    </div>
                  )}
                </div>
                {item.status === "success" && <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />}
                {item.status === "error" && (
                  <button
                    onClick={(e) => { e.stopPropagation(); retryUpload(item.id); }}
                    className="flex-shrink-0 text-xs font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  >
                    <RotateCcw className="w-3 h-3" /> Thử lại
                  </button>
                )}
                {(item.status === "success" || item.status === "error") && (
                  <button
                    onClick={(e) => { e.stopPropagation(); dismissUpload(item.id); }}
                    className="flex-shrink-0 text-slate-300 hover:text-slate-500"
                    aria-label="Xoá khỏi danh sách"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
